import { useState, useCallback, useRef, useEffect } from 'react'
import { X, Upload, Image as ImageIcon, Loader2, GripVertical, Search } from 'lucide-react'
import { adminApi } from '../lib/api'
import { transformUsers } from '../lib/transformers'
import { LocationPicker } from './maps/LocationPicker'
import type {
  CreatePostData,
  FileUploadProgress,
  UploadedFile,
  User,
  Trip,
  Event,
  Post,
} from '../types'

interface PostEditorProps {
  tripId?: string
  eventId?: string
  initialData?: CreatePostData
  onSuccess?: (postId: string) => void
  onCancel?: () => void
  mode?: 'create' | 'edit'
  postId?: string
  existingPost?: Post | null
}

interface Location {
  id: string
  name: string
  latitude: number
  longitude: number
  address?: string
}

type EditorFile = (UploadedFile & { caption?: string; fileId?: string })

export default function PostEditor({
  tripId,
  eventId,
  initialData,
  onSuccess,
  onCancel,
  mode = 'create',
  postId,
  existingPost,
}: PostEditorProps) {
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [allUsers, setAllUsers] = useState<User[]>([])
  const [tripParticipants, setTripParticipants] = useState<User[]>([])
  
  // Trip/Event search
  const [tripSearch, setTripSearch] = useState('')
  const [tripResults, setTripResults] = useState<Trip[]>([])
  const [selectedTrip, setSelectedTrip] = useState<Trip | null>(null)
  const [searchingTrips, setSearchingTrips] = useState(false)
  
  const [eventSearch, setEventSearch] = useState('')
  const [eventResults, setEventResults] = useState<Event[]>([])
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null)
  const [searchingEvents, setSearchingEvents] = useState(false)
  
  // Location
  const [tripLocations, setTripLocations] = useState<Location[]>([])
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null)
  const [showLocationPicker, setShowLocationPicker] = useState(false)
  const [customLocation, setCustomLocation] = useState<{ lat: number; lng: number; address?: string } | null>(null)
  
  const [loadingUsers, setLoadingUsers] = useState(true)
  const [description, setDescription] = useState(initialData?.description || existingPost?.description || '')
  const [privacy, setPrivacy] = useState<'public' | 'friends' | 'private'>(
    initialData?.privacy || (existingPost?.privacy as 'public' | 'friends' | 'private') || 'public'
  )
  const [uploads, setUploads] = useState<FileUploadProgress[]>([])
  const [uploadedFiles, setUploadedFiles] = useState<EditorFile[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)

  // Load users and trip data
  useEffect(() => {
    loadAllUsers()
    if (tripId) {
      loadTripParticipants()
      loadTripLocations()
    }
  }, [tripId])

  // Seed existing post data when editing
  useEffect(() => {
    if (mode !== 'edit' || !existingPost) return

    setDescription(existingPost.description || '')
    setPrivacy(existingPost.privacy)

    if (existingPost.location?.id) {
      setSelectedLocation(existingPost.location.id)
    }

    setUploadedFiles(
      existingPost.media.map((media): EditorFile => ({
        id: media.fileId || media.id,
        fileId: media.fileId || media.id,
        url: media.url || media.thumbnailUrl || '',
        thumbnailUrl: media.thumbnailUrl,
        fileType: media.mediaType === 'video' ? 'video/mp4' : 'image/jpeg',
        size: 0,
        filename: media.id,
        originalFilename: media.id,
        filePath: undefined,
        privacy: 'public',
        description: undefined,
        tags: [],
        caption: media.caption ?? undefined,
      }))
    )
  }, [mode, existingPost])

  const loadAllUsers = async () => {
    setLoadingUsers(true)
    try {
      const response = await adminApi.getUsers({ limit: 1000 })
      const rawUsers = response.data?.data?.users || response.data?.users || []
      const users = transformUsers(Array.isArray(rawUsers) ? rawUsers : [])
      setAllUsers(users)
      if (!selectedUser) {
        if (mode === 'edit' && existingPost) {
          const existing = users.find((user: User) => user.id === existingPost.userId)
          const nextUser = existing || users[0] || null
          setSelectedUser(nextUser)
          if (nextUser && error === 'Select a user before uploading files') {
            setError(null)
          }
        } else if (users.length > 0) {
          const nextUser = users[0]
          setSelectedUser(nextUser)
          if (error === 'Select a user before uploading files') {
            setError(null)
          }
        }
      }
    } catch (err) {
      console.error('Failed to load users:', err)
    } finally {
      setLoadingUsers(false)
    }
  }

  const normalizeUser = (raw: any): User => ({
    id: raw.id || raw.user_id || raw.userId || raw.user?.id,
    displayName:
      raw.displayName ||
      raw.display_name ||
      raw.username ||
      raw.email ||
      'Unknown user',
    email: raw.email || raw.user?.email || '',
    avatarUrl: raw.avatarUrl ?? raw.avatar_url ?? raw.user?.avatarUrl ?? raw.user?.avatar_url ?? null,
    verified: Boolean(raw.verified ?? raw.is_verified ?? false),
    createdAt: raw.createdAt || raw.created_at || new Date().toISOString(),
    isActive: Boolean(raw.isActive ?? raw.is_active ?? true),
    subscriptionType: raw.subscriptionType || raw.subscription_type || 'free',
  })

  const loadTripParticipants = async () => {
    if (!tripId) return
    try {
      const response = await adminApi.getTripParticipants(tripId)
      const rawParticipants =
        response.data?.data?.participants || response.data?.participants || []

      const participants = (Array.isArray(rawParticipants) ? rawParticipants : [])
        .map(normalizeUser)
        .filter(user => user.id)
      setTripParticipants(participants)

      const fallbackUser =
        participants.find(user => user.id === selectedUser?.id) ||
        allUsers.find(user => user.id === selectedUser?.id) ||
        participants[0] ||
        allUsers[0] ||
        null

      setSelectedUser(prev => prev || fallbackUser)
      if (fallbackUser && error === 'Select a user before uploading files') {
        setError(null)
      }
    } catch (err) {
      console.error('Failed to load trip participants:', err)
    }
  }

  const loadTripLocations = async () => {
    if (!tripId) return
    try {
      const response = await adminApi.getTripLocations(tripId)
      const locations = response.data?.data?.locations || response.data?.data || response.data || []
      setTripLocations(locations)
    } catch (err) {
      console.error('Failed to load trip locations:', err)
    }
  }

  // Search trips
  useEffect(() => {
    if (tripSearch.length < 2) {
      setTripResults([])
      return
    }

    const timer = setTimeout(async () => {
      setSearchingTrips(true)
      try {
        const response = await adminApi.getTrips({ search: tripSearch, limit: 10 })
        setTripResults(response.data?.data?.trips || [])
      } catch (err) {
        console.error('Trip search error:', err)
      } finally {
        setSearchingTrips(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [tripSearch])

  // Search events
  useEffect(() => {
    if (eventSearch.length < 2) {
      setEventResults([])
      return
    }

    const timer = setTimeout(async () => {
      setSearchingEvents(true)
      try {
        const response = await adminApi.getEvents({ search: eventSearch, limit: 10 })
        setEventResults(response.data?.data?.events || [])
      } catch (err) {
        console.error('Event search error:', err)
      } finally {
        setSearchingEvents(false)
      }
    }, 300)

    return () => clearTimeout(timer)
  }, [eventSearch])

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0) return

    const fileArray = Array.from(files)
    if (uploadedFiles.length + fileArray.length > 10) {
      setError('Maximum 10 files allowed')
      return
    }

    if (!selectedUser) {
      setError('Select a user before uploading files')
      return
    }

    // Initialize upload progress
    const newUploads: FileUploadProgress[] = fileArray.map(file => ({
      file,
      progress: 0
    }))
    setUploads(prev => [...prev, ...newUploads])

    // Upload files
    for (let i = 0; i < fileArray.length; i++) {
      const file = fileArray[i]
      try {
        const response = await adminApi.uploadFile(file, {
          trip_id: tripId,
          event_id: eventId,
          user_id: selectedUser.id,
          privacy: 'public'
        })

        const uploadedFile: EditorFile = {
          id: response.data.data.id,
          url: response.data.data.url,
          thumbnailUrl: response.data.data.thumbnailUrl,
          fileType: response.data.data.fileType || 'image',
          size: response.data.data.size || 0,
          width: response.data.data.width,
          height: response.data.data.height,
          fileId: response.data.data.id,
        }

        setUploadedFiles(prev => [...prev, { ...uploadedFile, caption: '' }])
        setUploads(prev => prev.filter(u => u.file !== file))
      } catch (err) {
        console.error('Upload error:', err)
        setUploads(prev => prev.map(u => 
          u.file === file 
            ? { ...u, error: 'Upload failed' }
            : u
        ))
      }
    }
  }, [tripId, eventId, privacy, uploadedFiles.length, selectedUser])

  const removeFile = useCallback((fileId: string) => {
    setUploadedFiles(prev => prev.filter(f => f.id !== fileId && f.fileId !== fileId))
  }, [])

  const handleDragStart = (index: number) => {
    setDraggedIndex(index)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    if (draggedIndex === null || draggedIndex === index) return

    const newFiles = [...uploadedFiles]
    const draggedFile = newFiles[draggedIndex]
    newFiles.splice(draggedIndex, 1)
    newFiles.splice(index, 0, draggedFile)
    
    setUploadedFiles(newFiles)
    setDraggedIndex(index)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
  }

  const determineContentType = (): 'text' | 'photo' | 'gallery' => {
    if (uploadedFiles.length === 0) return 'text'
    if (uploadedFiles.length === 1) return 'photo'
    return 'gallery'
  }

  const handleSubmit = async () => {
    if (!description.trim() && uploadedFiles.length === 0) {
      setError('Add description or upload files')
      return
    }

    if (!selectedUser) {
      setError('Select a user to post as')
      return
    }

    setIsSubmitting(true)
    setError(null)

    try {
      const postData: any = {
        user_id: selectedUser.id,
        privacy,
      }

      postData.content_type = contentType

      // Only add fields with actual values
      if (description.trim()) {
        postData.description = description.trim()
      }

      const finalTripId = selectedTrip?.id || tripId
      if (finalTripId) {
        postData.trip_id = finalTripId
      }

      const finalEventId = selectedEvent?.id || eventId
      if (finalEventId) {
        postData.event_id = finalEventId
      }

      if (selectedLocation) {
        postData.location_id = selectedLocation
      }

      if (customLocation) {
        postData.custom_location = {
          latitude: customLocation.lat,
          longitude: customLocation.lng,
          address: customLocation.address
        }
      }

      console.log('Creating post with data:', postData)
      if (uploadedFiles.length > 0) {
        postData.media = uploadedFiles.map((file, index) => ({
          file_id: file.fileId || file.id,
          order_index: index,
        }))
      }

      if (mode === 'edit' && postId) {
        await adminApi.updatePost(postId, postData)
        onSuccess?.(postId)
      } else {
        const response = await adminApi.createPost(postData)
        const payload = response.data?.data ?? response.data ?? null
        const createdId =
          payload?.post?.id ??
          payload?.id ??
          (typeof payload === 'string' ? payload : null)

        if (!createdId) {
          throw new Error('Missing post id in create response')
        }

        onSuccess?.(createdId)
      }
    } catch (err: any) {
      console.error('Post creation error:', err)
      setError(err.response?.data?.error || err.message || 'Failed to create post')
      setIsSubmitting(false)
      return
    }
    setIsSubmitting(false)
  }

  const contentType = determineContentType()

  return (
    <div className="space-y-4">
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Trip/Event Info */}
      {(tripId || eventId) && (
        <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="text-sm font-medium text-blue-900">
            {tripId && '🗺️ Post will be attached to this trip'}
            {eventId && '📅 Post will be attached to this event'}
          </div>
        </div>
      )}

      {/* User Selection */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Post as User *
        </label>
        
        {loadingUsers ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span className="text-sm">Loading users...</span>
          </div>
        ) : (
          <select
            value={selectedUser?.id || ''}
            onChange={(e) => {
              const user = allUsers.find(u => u.id === e.target.value)
              setSelectedUser(user || null)
              if (error === 'Select a user before uploading files') {
                setError(null)
              }
            }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            required
          >
            <option value="">Select user...</option>
            {tripParticipants.length > 0 && (
              <optgroup label="Trip Participants">
                {tripParticipants.map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} ({user.email})
                  </option>
                ))}
              </optgroup>
            )}
            <optgroup label={tripParticipants.length > 0 ? "All Users" : "Users"}>
              {allUsers
                .filter(u => !tripParticipants.find(p => p.id === u.id))
                .map((user) => (
                  <option key={user.id} value={user.id}>
                    {user.displayName} ({user.email})
                  </option>
                ))}
            </optgroup>
          </select>
        )}
      </div>

      {/* Content Type Badge */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-gray-500">Post type:</span>
        <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
          {contentType === 'text' && '📝 Text'}
          {contentType === 'photo' && '📷 Photo'}
          {contentType === 'gallery' && '🖼️ Gallery'}
        </span>
      </div>

      {/* Description */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Description
        </label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Share your thoughts..."
          rows={4}
          maxLength={5000}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
        />
        <div className="mt-1 text-xs text-gray-500 text-right">
          {description.length} / 5000
        </div>
      </div>

      {/* Privacy */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Privacy
        </label>
        <select
          value={privacy}
          onChange={(e) => setPrivacy(e.target.value as any)}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          <option value="public">Public</option>
          <option value="friends">Friends</option>
          <option value="private">Private</option>
        </select>
      </div>

      {/* Trip Search */}
      {!tripId && (
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trip (optional)
          </label>
          {selectedTrip ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
              <span className="flex-1 text-sm">{selectedTrip.title}</span>
              <button
                type="button"
                onClick={() => setSelectedTrip(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={tripSearch}
                  onChange={(e) => setTripSearch(e.target.value)}
                  placeholder="Search trips by title..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchingTrips && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" size={18} />
                )}
              </div>
              
              {tripResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {tripResults.map((trip) => (
                    <button
                      key={trip.id}
                      type="button"
                      onClick={() => {
                        setSelectedTrip(trip)
                        setTripSearch('')
                        setTripResults([])
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="font-medium text-sm">{trip.title}</div>
                      {trip.location?.address && (
                        <div className="text-xs text-gray-500 mt-1">📍 {trip.location.address}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {tripId && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Trip
          </label>
          <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg border border-gray-300">
            Already attached to this trip
          </p>
        </div>
      )}

      {/* Event Search */}
      {!eventId && (
        <div className="relative">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Event (optional)
          </label>
          {selectedEvent ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
              <span className="flex-1 text-sm">{selectedEvent.title}</span>
              <button
                type="button"
                onClick={() => setSelectedEvent(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  type="text"
                  value={eventSearch}
                  onChange={(e) => setEventSearch(e.target.value)}
                  placeholder="Search events by title..."
                  className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {searchingEvents && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 animate-spin text-gray-400" size={18} />
                )}
              </div>
              
              {eventResults.length > 0 && (
                <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  {eventResults.map((event) => (
                    <button
                      key={event.id}
                      type="button"
                      onClick={() => {
                        setSelectedEvent(event)
                        setEventSearch('')
                        setEventResults([])
                      }}
                      className="w-full px-4 py-2 text-left hover:bg-gray-50 border-b border-gray-100 last:border-0"
                    >
                      <div className="font-medium text-sm">{event.title}</div>
                      {event.location?.name && (
                        <div className="text-xs text-gray-500 mt-1">📍 {event.location.name}</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {eventId && (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Event
          </label>
          <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg border border-gray-300">
            Already attached to this event
          </p>
        </div>
      )}

      {/* Location Selection */}
      {tripLocations.length > 0 ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Location (optional)
          </label>
          <select
            value={selectedLocation || ''}
            onChange={(e) => setSelectedLocation(e.target.value || null)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">No location</option>
            {tripLocations.map((location) => (
              <option key={location.id} value={location.id}>
                📍 {location.name}
              </option>
            ))}
          </select>
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Custom Location (optional)
          </label>
          {customLocation ? (
            <div className="flex items-center gap-2 p-3 bg-gray-50 border border-gray-300 rounded-lg">
              <span className="flex-1 text-sm">
                📍 {customLocation.address || `${customLocation.lat}, ${customLocation.lng}`}
              </span>
              <button
                type="button"
                onClick={() => setCustomLocation(null)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setShowLocationPicker(true)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-left text-sm text-gray-500"
            >
              Pick location on map...
            </button>
          )}
        </div>
      )}

      {/* Location Picker Modal */}
      {showLocationPicker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-4 border-b border-gray-200 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Pick Location</h3>
              <button
                onClick={() => setShowLocationPicker(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>
            <div className="h-[500px]">
              <LocationPicker
                value={{
                  lat: customLocation?.lat || null,
                  lng: customLocation?.lng || null,
                  address: customLocation?.address || ''
                }}
                onChange={(location) => {
                  if (location.lat && location.lng) {
                    setCustomLocation({
                      lat: location.lat,
                      lng: location.lng,
                      address: location.address || ''
                    })
                  }
                  setShowLocationPicker(false)
                }}
              />
            </div>
          </div>
        </div>
      )}

      {/* File Upload Area */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Media (max 10 files)
        </label>
        
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept="image/*"
          onChange={(e) => handleFileSelect(e.target.files)}
          className="hidden"
        />

        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadedFiles.length >= 10}
          className="w-full p-8 border-2 border-dashed border-gray-300 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <div className="flex flex-col items-center gap-2 text-gray-500">
            <Upload className="w-8 h-8" />
            <span className="text-sm">Click to upload or drag and drop</span>
            <span className="text-xs">Images only (PNG, JPG, WEBP)</span>
          </div>
        </button>
      </div>

      {/* Upload Progress */}
      {uploads.length > 0 && (
        <div className="space-y-2">
          {uploads.map((upload, index) => (
            <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {upload.file.name}
                </p>
                {upload.error && (
                  <p className="text-xs text-red-600">{upload.error}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Uploaded Files Grid */}
      {uploadedFiles.length > 0 && (
        <div className="grid grid-cols-2 gap-4">
          {uploadedFiles.map((file, index) => (
            <div
              key={file.id}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={`relative group border-2 rounded-lg overflow-hidden transition-all ${
                draggedIndex === index ? 'border-blue-500 opacity-50' : 'border-gray-200'
              }`}
            >
              {/* Drag Handle */}
              <div className="absolute top-2 left-2 z-10 p-1 bg-white/90 rounded cursor-move">
                <GripVertical className="w-4 h-4 text-gray-600" />
              </div>

              {/* Remove Button */}
              <button
                onClick={() => removeFile(file.id)}
                className="absolute top-2 right-2 z-10 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <X className="w-4 h-4" />
              </button>

              {/* Image Preview */}
              <div className="aspect-square bg-gray-100 flex items-center justify-center">
                {file.url ? (
                  <img
                    src={file.thumbnailUrl || file.url}
                    alt=""
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon className="w-12 h-12 text-gray-400" />
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Actions */}
      <div className="flex justify-end gap-3 pt-4 border-t">
        {onCancel && (
          <button
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          onClick={handleSubmit}
          disabled={isSubmitting || !selectedUser || (!description.trim() && uploadedFiles.length === 0)}
          className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
          {isSubmitting ? 'Creating...' : 'Create Post'}
        </button>
      </div>
    </div>
  )
}
